// User Types
export interface User {
  id: number;
  email: string;
  name: string;
  profilePictureUrl?: string;
  createdAt: Date;
  lastLoginAt: Date;
  /** CL1-CL3 / F4 — true when this user has accepted the current legal docs version. */
  legalAccepted?: boolean;
  /** Version string of the legal docs the user accepted; matches backend LEGAL_VERSION. */
  legalVersion?: string;
}

export interface UserSettings {
  speechAlertEnabled: boolean;
  telegramNotificationEnabled: boolean;
  telegramBotToken?: string;
  telegramChatId?: string;
  telegramReportIntervalMinutes?: number;
  autoTradeEnabled: boolean;
  themePreference: 'light' | 'dark';
  defaultOrderQuantity: number;
  /** Multi-entry scale-in default: number of averaging-down entry levels (1..5). */
  entrySplitCount?: number;
  defaultOrderType: OrderType;
  maxOrderAmount?: number;
  portfolioReservePercent?: number;
  requireOrderConfirmation: boolean;
  autoRefreshEnabled: boolean;
  refreshIntervalSeconds: number;
  favoriteSymbols?: string[];
  // In-house signal mode
  inHouseSignalMode?: boolean;
  rockBandEnabled?: boolean;
  priceLevelAutoExecute?: boolean;
  // Global order kill switch — when false, broker placeOrder calls are rejected at source
  orderPlacementEnabled?: boolean;
  // R-multiple (mfe / initialRisk) threshold before trailing SL activates
  trailSlActivationR?: number;
  // Phase B — daily-loss circuit-breaker
  dailyLossCap?: number | null;
  dailyLossCapEnabled?: boolean;
  // Liquidation guard. NOTE these are a different axis from portfolioReservePercent
  // above: the reserve is FREE margin as a % of equity (and it BLOCKS trades),
  // whereas MMR is MAINTENANCE margin as a % of collateral (and it only warns).
  liquidationGuardEnabled?: boolean;
  liquidationWarnMmrPercent?: number;
  liquidationTargetMmrPercent?: number;
}

// Trading Types
export interface Instrument {
  id: number;
  symbol: string;
  name: string;
  exchange: string;
  currency: string;
  lotSize: number;
  tickSize: number;
  leverage: number;
  instrumentType: InstrumentType;
  currentPosition: 'NONE' | 'LONG' | 'SHORT';
  indTracked: boolean;
  indLiveFeed?: boolean;
  indAutoTradeLong: boolean;
  indAutoTradeShort: boolean;
  lastPrice?: number;
  change24h?: number;
  change24hPercent?: number;
  // Extended fields
  tradeSegment?: string;
  tradeMode?: string;
  eqInvestmentAmount?: number;
  futInvestmentAmount?: number;
  optInvestmentAmountBuy?: number;
  optInvestmentAmountSell?: number;
  wlLong?: boolean;
  wlShort?: boolean;
  nbuy?: number | null;
  nshort?: number | null;
  bbuy?: number | null;
  bshort?: number | null;
  currentPrice?: number;
}

export interface Alert {
  id: number;
  type: AlertType;
  status: AlertStatus;
  priority: AlertPriority;
  title: string;
  message: string;
  symbol?: string;
  triggerPrice?: number;
  currentPrice?: number;
  allowMarketEntry: boolean;
  marketEntryPrice?: number;
  isAcknowledged: boolean;
  isSimulation: boolean;
  createdAt: Date;
  triggeredAt?: Date;
  acknowledgedAt?: Date;
  expiryDate?: Date;
  renotifyAt?: number;
  renotifyDirection?: string;
  renotifyTriggered?: boolean;
  customEntryPrice?: number;
  customSL?: number;
  customTarget?: number;
  parsedData?: {
    action?: string;
    strategy?: string;
    originalPrice?: number;
    signalStrength?: number;
    exitSL?: number;
    exitReason?: string;
    condition?: string;
    timeframe?: string;
    entryLevel?: number;
    isl?: number;
    confirmed?: string;
    [key: string]: any;
  };
  // Raw TradingView webhook payload. Present only on TV-originated alerts;
  // in-house scheduled scans leave this null. Used by Origin filter.
  rawAlert?: any;
}

export interface Trade {
  id: number;
  tradeId: string;
  symbol: string;
  /** PAPER chart trade — simulated by the app, never a broker position. */
  isSimulation?: boolean;
  side: 'LONG' | 'SHORT';
  status: TradeStatus;
  quantity: number;
  averageEntryPrice: number;
  currentPrice: number;
  unrealizedPnl: number;
  realizedPnl: number;
  dayPnl: number;
  totalPnl: number;
  pnlPercent: number;
  stopLoss?: number;
  manualStopLoss?: number;
  target?: number;
  lotSize?: number;
  closePrice?: number;
  closeQuantity?: number;
  closeMethod?: string;
  hasIssue?: boolean;
  issueDescription?: string;
  slTriggerEnabled?: boolean;
  orders: Order[];
  createdAt: Date;
  executedAt?: Date;
  closedAt?: Date;
  lastUpdated: Date;
}

export interface Order {
  id: number;
  orderId: string;
  brokerOrderId?: string;
  tradeId?: number;
  trade?: { id: number };
  symbol: string;
  orderType: OrderType;
  side: 'BUY' | 'SELL';
  status: OrderStatus;
  quantity: number;
  filledQuantity: number;
  price?: number;
  stopPrice?: number;
  commission: number;
  tax: number;
  totalCost: number;
  brokerResponse?: Record<string, any>;
  createdAt: Date;
  executedAt?: Date;
}

// Dashboard Types
export interface DashboardSummary {
  pnl: {
    today: { amount: number; percentage: number };
    week: { amount: number; percentage: number };
    month: { amount: number; percentage: number };
  };
  portfolio: {
    totalValue: number;
    /** Round 27 — per-broker portfolio split. Either may be 0 when the
     *  user lacks credentials for that broker; totalValue is the sum. */
    deltaValue?: number;
    zerodhaValue?: number;
    availableMargin: number;
    usedMargin: number;
    /**
     * Percent of portfolio value currently deployed, or NULL when it cannot be determined
     * -- e.g. a broker reported its account value but not its free balance. Render null as
     * unknown; 0 would read as 'nothing at risk', which is a different claim entirely.
     */
    marginUtilization: number | null;
  };
  positions: {
    total: number;
    long: number;
    short: number;
    profitable: number;
    loss: number;
  };
  recentAlerts: Alert[];
  recentTrades: Trade[];
  /** Open PAPER chart trades (isSimulation) — shown in their own Dashboard sub-section. */
  paperTrades?: Trade[];
  parkedAlerts: Alert[];
}

export interface Event {
  id: number;
  title: string;
  date: Date;
  type: string;
  description?: string;
}

// Enums
export enum OrderType {
  MARKET = 'MARKET',
  LIMIT = 'LIMIT',
  STOP = 'STOP',
  STOP_LIMIT = 'STOP_LIMIT'
}

export enum OrderStatus {
  PENDING = 'PENDING',
  FILLED = 'FILLED',
  PARTIAL = 'PARTIAL',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED'
}

export enum AlertType {
  PRICE = 'PRICE',
  TECHNICAL = 'TECHNICAL',
  VOLUME = 'VOLUME',
  RISK = 'RISK',
  SYSTEM = 'SYSTEM',
  NEWS = 'NEWS',
  STRATEGY = 'STRATEGY'
}

export enum AlertStatus {
  PENDING = 'PENDING',
  TRIGGERED = 'TRIGGERED',
  EXPIRED = 'EXPIRED',
  ACKNOWLEDGED = 'ACKNOWLEDGED'
}

export enum AlertPriority {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW'
}

export enum TradeStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  PARTIAL = 'PARTIAL',
  LIMBO = 'LIMBO'
}

export enum InstrumentType {
  EQUITY = 'EQUITY',
  FUTURE = 'FUTURE',
  OPTION = 'OPTION'
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  name: string;
  password: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
}
